import { z } from "zod";
export { PRODUCTION_HOLD } from "./holds.js";

/**
 * Produktion (Konfektionierung): Kunden beauftragen Logiful, aus Komponenten und Hilfsmaterial einen Fertigartikel auf
 * Vorrat zu bauen. Grundlage ist die Produktionsstückliste des Fertigartikels (Komponenten, Kunden-Material,
 * Logiful-Material, Arbeitsschritte mit Preis). Anders als ein Set hat der Fertigartikel danach eigenen Bestand.
 */

const money = z.number().int().min(0).max(100_000_000);

export const PRICE_MODES = ["none", "per_unit", "per_run", "per_minute"] as const;
export type PriceMode = (typeof PRICE_MODES)[number];
export const PRICE_MODE_LABEL: Record<PriceMode, string> = {
  none: "ohne Preis",
  per_unit: "je Stück",
  per_run: "je Auftrag (pauschal)",
  per_minute: "je Minute",
};

export const productionStepSchema = z.object({
  id: z.string().trim().min(1).max(40),
  title: z.string().trim().min(1, "Arbeitsschritt braucht einen Namen").max(120),
  instructions: z.string().trim().max(4000).default(""),
  /** Vorgabezeit je Fertigstück (Minuten) – Grundlage für „je Minute“ und die Planung */
  minutesPerUnit: z.number().min(0).max(600).nullable().default(null),
  priceMode: z.enum(PRICE_MODES).default("none"),
  /** Cent je Stück / je Auftrag / je Minute */
  priceMinor: money.default(0),
});
export type ProductionStep = z.infer<typeof productionStepSchema>;

export const bomComponentSchema = z.object({
  productId: z.string().uuid(),
  sku: z.string().trim().min(1).max(120),
  /** Menge je Durchlauf (ergibt outputQuantity Fertigstücke) */
  quantity: z.number().positive().max(100_000),
  /** component = Ware, material = Hilfsmaterial des Kunden (Karton, Flyer, Etikett …) */
  role: z.enum(["component", "material"]).default("component"),
});
export type BomComponent = z.infer<typeof bomComponentSchema>;

/** Hilfsmaterial, das Logiful stellt (wird berechnet, nicht als Kundenbestand geführt) */
export const logifulMaterialSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Menge je Fertigstück */
  quantity: z.number().positive().max(10_000),
  unit: z.string().trim().max(20).default("Stk."),
  priceMinor: money.default(0),
});
export type LogifulMaterial = z.infer<typeof logifulMaterialSchema>;

/**
 * Automatisch nachproduzieren: fällt der verfügbare Bestand des Fertigartikels (inkl. Zulauf und offener Produktion)
 * unter den Mindestbestand, legt Connect selbst einen Produktionsauftrag an.
 */
export const replenishSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** Mindestbestand (Fertigstücke) */
    minStock: z.number().int().min(1).max(1_000_000).default(100),
    /** so viel wird je Auftrag produziert (mindestens bis zum Mindestbestand) */
    batchQuantity: z.number().int().min(1).max(1_000_000).default(100),
    /** Logiful nimmt automatisch an (Preis aus der Stückliste) – stellt nur Logiful ein */
    autoAccept: z.boolean().default(false),
  })
  .default({ enabled: false, minStock: 100, batchQuantity: 100, autoAccept: false });
export type ProductionReplenish = z.infer<typeof replenishSchema>;

export const productionBomSchema = z
  .object({
    /** Fertigstücke je Durchlauf (z. B. 1 Set, 24er-Tray) */
    outputQuantity: z.number().int().min(1).max(10_000).default(1),
    components: z.array(bomComponentSchema).max(100).default([]),
    logifulMaterials: z.array(logifulMaterialSchema).max(50).default([]),
    steps: z.array(productionStepSchema).max(50).default([]),
    /** Muster vor der Serie durch den Kunden freigeben lassen */
    sampleRequired: z.boolean().default(false),
    note: z.string().trim().max(2000).default(""),
    replenish: replenishSchema,
  })
  .superRefine((b, ctx) => {
    if (!b.components.some((c) => c.role === "component")) ctx.addIssue({ code: "custom", message: "Mindestens eine Komponente (Ware) ist nötig", path: ["components"] });
    const ids = b.components.map((c) => c.productId);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "Jeder Artikel nur einmal in der Stückliste", path: ["components"] });
    const stepIds = b.steps.map((s) => s.id);
    if (new Set(stepIds).size !== stepIds.length) ctx.addIssue({ code: "custom", message: "Arbeitsschritte doppelt", path: ["steps"] });
  });
export type ProductionBom = z.infer<typeof productionBomSchema>;

/** Bedarf je Artikel der Stückliste für eine Menge Fertigstücke (aufgerundet – angebrochene Durchläufe brauchen ganze Teile) */
export function componentNeeds(bom: Pick<ProductionBom, "outputQuantity" | "components">, quantity: number): { productId: string; sku: string; role: BomComponent["role"]; quantity: number }[] {
  return bom.components.map((c) => ({ productId: c.productId, sku: c.sku, role: c.role, quantity: Math.ceil((c.quantity * quantity) / bom.outputQuantity - 1e-9) }));
}

export const quoteLineSchema = z.object({
  label: z.string().trim().min(1).max(160),
  quantity: z.number().min(0).max(10_000_000),
  unit: z.string().trim().max(20).default("Stk."),
  unitPriceMinor: z.number().int().min(-100_000_000).max(100_000_000),
});
export type QuoteLine = z.infer<typeof quoteLineSchema>;

export const productionQuoteSchema = z.object({
  lines: z.array(quoteLineSchema).max(60),
  note: z.string().trim().max(1000).default(""),
});
export type ProductionQuote = z.infer<typeof productionQuoteSchema>;

export const lineTotal = (l: Pick<QuoteLine, "quantity" | "unitPriceMinor">) => Math.round(l.quantity * l.unitPriceMinor);
export const quoteTotal = (q: Pick<ProductionQuote, "lines">) => q.lines.reduce((s, l) => s + lineTotal(l), 0);

/**
 * Preisvorschlag aus der Stückliste: Arbeitsschritte je Stück / pauschal / je Minute (Vorgabezeit × Menge) und
 * Logiful-Material je Fertigstück. Logiful kann ihn bei der Annahme beliebig anpassen (Angebot je Auftrag).
 */
export function estimateQuote(bom: Pick<ProductionBom, "steps" | "logifulMaterials">, quantity: number): ProductionQuote {
  const lines: QuoteLine[] = [];
  for (const s of bom.steps) {
    if (s.priceMode === "per_unit") lines.push({ label: s.title, quantity, unit: "Stk.", unitPriceMinor: s.priceMinor });
    else if (s.priceMode === "per_run") lines.push({ label: s.title, quantity: 1, unit: "pauschal", unitPriceMinor: s.priceMinor });
    else if (s.priceMode === "per_minute") lines.push({ label: s.title, quantity: Math.round((s.minutesPerUnit ?? 0) * quantity * 100) / 100, unit: "Min.", unitPriceMinor: s.priceMinor });
  }
  for (const m of bom.logifulMaterials) if (m.priceMinor > 0) lines.push({ label: `${m.name} (Material Logiful)`, quantity: Math.round(m.quantity * quantity * 100) / 100, unit: m.unit, unitPriceMinor: m.priceMinor });
  return { lines, note: "" };
}

/** geplante Arbeitszeit in Minuten (Summe der Vorgabezeiten) */
export const plannedMinutes = (bom: Pick<ProductionBom, "steps">, quantity: number) => Math.round(bom.steps.reduce((s, x) => s + (x.minutesPerUnit ?? 0), 0) * quantity);

/** Produzieren (Komponenten → Fertigartikel) oder Auflösen (Fertigartikel → Komponenten zurück auf Lager) */
export const PRODUCTION_KINDS = ["assembly", "disassembly"] as const;
export type ProductionKind = (typeof PRODUCTION_KINDS)[number];

/** Was beim Auftrag aus dem Lager geht: beim Produzieren die Komponenten, beim Auflösen der Fertigartikel */
export function outgoingFor(kind: ProductionKind, bom: Pick<ProductionBom, "outputQuantity" | "components">, finished: { productId: string; sku: string }, quantity: number) {
  return kind === "assembly" ? componentNeeds(bom, quantity) : [{ productId: finished.productId, sku: finished.sku, role: "component" as const, quantity }];
}

/** Was beim Auflösen voraussichtlich zurückkommt (Komponenten; Kunden-Material meist nicht wiederverwendbar → 0) */
export function disassemblyYield(bom: Pick<ProductionBom, "outputQuantity" | "components">, quantity: number) {
  return componentNeeds(bom, quantity).map((n) => ({ ...n, expected: n.role === "component" ? Math.floor((bom.components.find((c) => c.productId === n.productId)!.quantity * quantity) / bom.outputQuantity) : 0 }));
}

/**
 * Nachproduktion nötig? verfügbar = Lagerbestand − Reservierungen + Zulauf + offene Produktion.
 * Menge: Auffüllmenge, mindestens so viel, dass der Mindestbestand wieder erreicht ist.
 */
export function replenishQuantity(r: ProductionReplenish, available: number): number {
  if (!r.enabled || available >= r.minStock) return 0;
  return Math.max(r.batchQuantity, r.minStock - available);
}

export const PRODUCTION_STATUSES = ["requested", "accepted", "in_production", "completed", "cancelled", "rejected"] as const;
export type ProductionStatus = (typeof PRODUCTION_STATUSES)[number];
export const PRODUCTION_STATUS_LABEL: Record<ProductionStatus, string> = {
  requested: "Beauftragt",
  accepted: "Angenommen",
  in_production: "In Produktion",
  completed: "Fertig gemeldet",
  cancelled: "Storniert",
  rejected: "Abgelehnt",
};
/** Komponenten noch reserviert, aber nicht beim Lager angefordert */
export const PRODUCTION_WAITING: readonly ProductionStatus[] = ["requested", "accepted"];

export const SAMPLE_STATUSES = ["pending", "submitted", "approved", "rejected"] as const;
export type SampleStatus = (typeof SAMPLE_STATUSES)[number];


/** frühestes MHD der verbrauchten Komponenten → MHD des Fertigartikels (Vorschlag) */
export function earliestBestBefore(dates: (string | null | undefined)[]): string | null {
  const d = dates.filter((x): x is string => !!x).sort();
  return d[0] ?? null;
}
